import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import UserRowSkeleton from '../components/Skeleton/UserRowSkeleton';
import Swal from "sweetalert2";

const formatCurrency = (value = 0) =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Users: React.FC = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    uid: '',
    username: '',
    password: '',
  });

  useEffect(() => {
    fetchUsersData();
  }, [page]);

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/getUserData', {
        params: { page, limit },
      });

      setUsers(res.data.users || []);
      setTotalUsers(res.data.totalUsers);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id: number, name: string) => {
    return
    await api.delete('/deleteUser', { data: { id } });
    setSnackbar(`User ${name} blocked`);
    fetchUsersData();
    setTimeout(() => setSnackbar(null), 3000);
  };

  const handleOpenModal = (user: User) => {
    setEditUserData({ uid: String(user.id), username: user.name, password: '' });
    setIsModalOpen(true);
  };

  const handleEditUser = async () => {
    await api.put('/editProfile', {
      uid: editUserData.uid,
      username: editUserData.username,
      password: editUserData.password || undefined,
    });

    setSnackbar('User updated successfully');
    fetchUsersData();
    setIsModalOpen(false);
    setTimeout(() => setSnackbar(null), 3000);
  };



  const handleBlockUnBlockUser = async (uid, isBlocked) => {
    let block_reason = null;

    // If blocking → ask for reason
    if (!isBlocked) {
      const result = await Swal.fire({
        title: "Block User",
        input: "textarea",
        inputLabel: "Reason for blocking",
        inputPlaceholder: "Enter block reason...",
        inputAttributes: {
          maxlength: 255,
        },
        showCancelButton: true,
        confirmButtonText: "Block",
        confirmButtonColor: "#d33",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) {
            return "Block reason is required";
          }
        },
      });

      if (!result.isConfirmed) return;

      block_reason = result.value;
    }

    await api.put("/blockUser", {
      uid,
      is_blocked: !isBlocked,
      block_reason,
    });

    fetchUsersData();
    setIsModalOpen(false);
    setSnackbar("User status updated");

    setTimeout(() => setSnackbar(null), 3000);
  };

  const handleMakeCollaborator = async (uid, isCollaborator) => {
    try {

      //are you sure to make collaborator
      const result = await Swal.fire({
        title: "Make Collaborator",
        text: "Are you sure you want to make this user a collaborator?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (!result.isConfirmed) return;

      await api.put("/makeCollaborator", {
        uid,
        is_collaborator: !isCollaborator,
      });

      fetchUsersData();
      setIsModalOpen(false);
      setSnackbar("User status updated");

      setTimeout(() => setSnackbar(null), 3000);
    } catch (err) {
      console.log(err)
    }
  };


  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(u.id).includes(searchTerm)
  );

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            User Management
          </h1>
          <p className="text-sm text-gray-500">
            Manage user accounts and their settings.
          </p>
        </div>
        <span className="text-sm text-gray-500">
          Total Users: {totalUsers}
        </span>
      </div>

      {snackbar && (
        <div className="mb-4 rounded bg-green-100 text-green-800 px-4 py-2">
          {snackbar}
        </div>
      )}

      <input
        className="mb-4 w-full max-w-md rounded border px-4 py-2"
        placeholder="Search by name, email or UID"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              {[
                'S.No',
                'Name',
                'Email',
                'Password',
                // 'UID',
                'Referral Code',
                'Refree',
                'Deposit',
                'Withdrawal',
                'Balance',
                'Actions',
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <UserRowSkeleton columns={9} rows={10} />
            ) : (
              filteredUsers.map((user, i) => {
                const deposit = user.actual_total_deposit_amount || 0;
                const withdrawal = user.actual_total_withdrawal_amount || 0;
                const balance = user.available_inr || 0;

                return (
                  <tr key={user.id} className="border-t">
                    <td className="px-4 py-2">{(page - 1) * limit + i + 1}</td>
                    <td className="px-4 py-2 font-medium">{user.name}</td>
                    <td className="px-4 py-2">{user.email}</td>
                    <td className="px-4 py-2">{user.password2}</td>
                    {/* <td className="px-4 py-2">{user.id}</td> */}
                    <td className="px-4 py-2">{user.referalcode}</td>
                    <td className="px-4 py-2">{user?.refree || "N/A"}</td>
                    <td className="px-4 py-2 text-green-600">
                      {formatCurrency(deposit)}
                    </td>
                    <td className="px-4 py-2 text-red-600">
                      {formatCurrency(withdrawal)}
                    </td>
                    <td
                      className={`px-4 py-2 font-semibold ${balance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                    >
                      {formatCurrency(balance)}
                    </td>
                    <td className="px-4 py-2 space-x-2 flex justify-between items-center">
                      <button
                        onClick={() => handleOpenModal(user)}
                        className="border px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => navigate(`/users/profile/${user.id}`)}
                        className="border border-green-300 text-green-600 px-3 py-1 rounded"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleBlockUnBlockUser(user.id, user.is_blocked)}
                        className={`border ${user.is_blocked ? "border-yellow-300 text-yellow-600" : "border-red-300 text-red-600"} px-3 py-1 rounded`}
                      >
                        {user.is_blocked ? "Unblock" : "Block"}
                      </button>
                      {/* make agent button */}
                      <button
                        onClick={() => handleMakeCollaborator(user.id, user.is_collaborator)}
                        className={`border ${user.is_collaborator ? "border-yellow-300 text-yellow-600" : "border-red-300 text-red-600"} px-3 py-1 rounded`}
                      >
                        {user.is_collaborator ? "Collaborator" : "Make Collaborator"}
                      </button>

                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span className="px-3 py-1">
          {page} / {totalPages}
        </span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Edit Modal (unchanged UI) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <div className="bg-white p-6 rounded w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">Edit User</h2>

            <input
              className="w-full mb-3 border px-3 py-2"
              value={editUserData.username}
              onChange={(e) =>
                setEditUserData({ ...editUserData, username: e.target.value })
              }
            />

            <input
              type="password"
              placeholder="New password (optional)"
              className="w-full mb-4 border px-3 py-2"
              value={editUserData.password}
              onChange={(e) =>
                setEditUserData({ ...editUserData, password: e.target.value })
              }
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                className="px-4 py-2 bg-indigo-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
